export type AuthErrorType =
  | 'accountAlreadyExists'
  | 'doesNotMatchRules'
  | 'noAccountFound'
  | 'geolocationFirewall'
  | 'classlinkSSOError'
  | 'feideSSOError'
  | 'cleverSSOError'
  | 'googleSSOError'
  | 'invalidResponseReceived'
  // New passwordless / direct-OAuth experience (CURI-334):
  | 'oauthCallbackError' // generic OAuth redirect failure (provider handshake)
  | 'accessDenied' // user/provider denied consent, or signIn returned false
  | 'sessionExpired' // RefreshAccessTokenError surfaced to the user
  | 'magicLinkInvalid' // wrong / expired / consumed code (collapsed by backend)
  | 'rateLimited' // too many magic-link requests / verify attempts (429)
  | 'emailSendFailed' // magic-link request failed to send
  | 'unknown';

export interface AuthError {
  type: AuthErrorType;
  message?: string;
  originalError?: string; // For debugging purposes
  correlationId?: string; // Support reference id, shared with the backend logs
  timestamp?: string; // When the upstream error was reported
}

export class AuthenticationError extends Error {
  public readonly authError: AuthError;
  public readonly action?: string;

  constructor(authError: AuthError, action?: string) {
    const message = `Authentication Error [${authError.type}]: ${authError.message ||
      'Authentication failed'}`;
    super(message);

    this.name = 'AuthenticationError';
    this.authError = authError;
    this.action = action;

    // Ensure proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, AuthenticationError.prototype);
  }

  // Helper method to get custom attributes for New Relic
  getCustomAttributes() {
    return {
      errorType: this.authError.type,
      correlationId: this.authError.correlationId,
      originalError: this.authError.originalError,
      action: this.action,
      customMessage: this.authError.message,
    };
  }
}
